<template>
  <div 
    class="group relative h-full rounded-xl overflow-hidden glass-effect border border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer"
    @click="$emit('click')"
  >
    <!-- Card Background Gradient -->
    <div :class="`absolute inset-0 bg-gradient-to-br ${card.color} opacity-90 group-hover:opacity-100 transition-opacity duration-300`"></div>
    
    <!-- Subtle Pattern -->
    <div class="absolute inset-0 opacity-5 pattern-grid"></div>
    
    <!-- Card Content -->
    <div class="relative z-10 p-6 sm:p-8 h-full flex flex-col">
      <!-- Icon Container -->
      <div class="mb-4 sm:mb-6">
        <div class="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-white/10 backdrop-blur-sm">
          <svg class="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="card.icon" />
          </svg>
        </div>
      </div>
      
      <!-- Title -->
      <h3 class="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 leading-tight">
        {{ $t(card.title) }}
      </h3>
      
      <!-- Description -->
      <p class="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6 flex-grow leading-relaxed">
        {{ $t(card.description) }}
      </p>
      
      <!-- Action Button -->
      <div class="mt-auto">
        <button 
          :class="`w-full py-3 px-4 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-medium text-sm sm:text-base transition-all duration-300 group-hover:translate-x-1 flex items-center justify-center gap-2`"
        >
          {{ $t(card.buttonText) }}
          <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
      
      <!-- Hover Indicator -->
      <div class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </div>
    
    <!-- Corner Accents -->
    <div class="absolute top-0 left-0 w-8 h-8 border-t border-l border-white/20 rounded-tl-xl"></div>
    <div class="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/20 rounded-tr-xl"></div>
    <div class="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white/20 rounded-bl-xl"></div>
    <div class="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white/20 rounded-br-xl"></div>
  </div>
</template>

<script>
export default {
  name: 'ActionCard',
  props: {
    card: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      default: 0
    }
  },
  emits: ['click']
};
</script>

<style scoped>
.pattern-grid {
  background-image: 
    linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

.glass-effect {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Smooth entrance animation */
.action-card-enter {
  opacity: 0;
  transform: translateY(20px);
}

.action-card-enter-active {
  transition: all 0.5s ease;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .action-card {
    padding: 1.5rem;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .glass-effect {
    border: 2px solid white;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .group-hover\:translate-x-1,
  .transition-all,
  .transition-opacity,
  .transition-transform {
    transition: none;
  }
}
</style>